import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { extractApiError } from "../lib/apiErrors";
import { useT, useLanguage, useLocalized } from "../lib/LanguageContext";
import { Input } from "../components/ui/input";
import LoadFailure from "../components/ui/LoadFailure";
import { NoAccessPanel, isForbidden } from "./NoAccess";
import { useOperator } from "./OperatorContext";
import { mayReadGlobalTab } from "./screenAccess";
import { Loader2, Search } from "lucide-react";
import TenantPicker from "./TenantPicker";
import { TABS, valueKey } from "./globalDataTabs";
import GlobalDataTable from "./GlobalDataTable";
import { totalKey } from "./globalDataCopy";
import { SEARCH_MAX_LENGTH, useDebouncedSearch } from "./useDebouncedSearch";

/**
 * Super admin -> Global Data: a read-only, paged, sortable, filterable window on every
 * practice's data (docs/auth-and-admin/global-data-contract.md).
 *
 * The backend owns each row's shape; this page owns only layout. All twelve tabs share one
 * state machine (practice / search / status / sort / page) and one table, driven by the
 * column configs in `globalDataTabs.jsx`. Each tab is its own read with its own permission;
 * the operator is offered the tabs they can read (screenAccess.js) and the page opens on the
 * first of them.
 */

const PAGE_SIZE = 25;
const SELECT_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

const tabByKey = (key) => TABS.find((x) => x.key === key);

/**
 * The sentence for a failed read. A `detail` the server sent is kept (`extractApiError`
 * localises the codes it knows); anything else, a non-JSON 500, a dropped connection, an
 * unreadable 200, is the page's own localized sentence. `extractApiError` ends on
 * `e.message`, which for axios is a developer string ("Request failed with status code
 * 500"), so it must not be asked when there is no `detail` to extract.
 */
const loadErrorMessage = (error, fallback, tc) => {
  const detail = error?.response?.data?.detail;
  return detail ? extractApiError(error, fallback, tc) : fallback;
};

/** Asc -> desc -> asc on the same column; a new column starts ascending. */
const nextSort = (current, key) => (current === key ? `-${key}` : key);

export default function GlobalDataView() {
  const t = useT("superadmin");
  const tc = useT("common");
  const loc = useLocalized();
  const { lang } = useLanguage();

  const operator = useOperator();
  // Fixed for the page's life: the operator is read once per page load.
  const tabs = useMemo(() => TABS.filter((x) => mayReadGlobalTab(x.key, operator)), [operator]);
  const [tabKey, setTabKey] = useState(() => tabs[0]?.key ?? null);
  const tab = tabByKey(tabKey); // undefined when the operator can read no tab
  const [tenantId, setTenantId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState(tab?.defaultSort);
  const [page, setPage] = useState(1);
  // `tab` is stored with the rows so a slow read of the previous tab can never be rendered
  // under the new tab's columns.
  const [result, setResult] = useState(null); // { tab, total, items }
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  // The server is asked once the operator stops typing; a new query starts at page 1.
  const [search, setSearch, q, commitSearch] = useDebouncedSearch(() => setPage(1));

  useEffect(() => {
    if (!tabKey) return undefined;
    let cancelled = false;
    const params = { sort, limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
    if (tenantId) params.tenant_id = tenantId;
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    setLoading(true);
    api.get(`/super-admin/global/${tabKey}`, { params })
      .then((r) => {
        // A 200 that is not the list (the production proxy answers 200 + index.html for an
        // unknown /api path) is a failed read, not an empty table.
        if (!r.data || !Array.isArray(r.data.items) || !Number.isFinite(r.data.total)) {
          throw Object.assign(new Error(), { name: "PayloadShapeError" });
        }
        if (cancelled) return;
        setResult({ tab: tabKey, total: r.data.total, items: r.data.items });
        setLoadError(null);
      })
      .catch((e) => { if (!cancelled) setLoadError(e || new Error()); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tabKey, tenantId, q, statusFilter, sort, page, attempt]);

  // An explicit refresh: bumps `attempt`, which the read effect depends on, so the list is
  // read again even when no other input changed (React skips a setState to an equal value,
  // which is why Enter on an unchanged term used to read nothing).
  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  const changeTab = (key) => {
    // The already-active tab: a refresh that keeps the filters and the rows on screen.
    if (key === tabKey) { refresh(); return; }
    setTabKey(key);
    setStatusFilter("");
    setSort(tabByKey(key).defaultSort);
    setPage(1);
    setResult(null);
    setLoadError(null);
  };
  const changeTenant = (value) => { setTenantId(value); setPage(1); };
  const changeStatus = (value) => { setStatusFilter(value); setPage(1); };
  const changeSort = (key) => { setSort((cur) => nextSort(cur, key)); setPage(1); };
  // Enter is an explicit "read this term from the first page", whether or not the term changed.
  const submitSearch = () => { commitSearch(); setPage(1); refresh(); };

  const ctx = useMemo(() => ({ t, lang, loc }), [t, lang, loc]);
  const shown = result && result.tab === tabKey ? result : null;

  const heading = (
    <div className="mb-8">
      <h1 className="font-sans font-semibold text-4xl mb-2 text-slate-800">{t("gdv.title")}</h1>
      <p className="text-slate-500">{t("gdv.subtitle")}</p>
    </div>
  );
  // No tab to offer (a permissions override can leave a super admin here): no tab strip, no
  // filters, no read.
  if (!tab) {
    return (
      <div data-testid="sa-global-data">
        {heading}
        <NoAccessPanel testId="gdv-forbidden" />
      </div>
    );
  }

  return (
    <div data-testid="sa-global-data">
      {heading}

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tabDef) => {
          const Icon = tabDef.icon;
          return (
            <button
              key={tabDef.key}
              onClick={() => changeTab(tabDef.key)}
              data-testid={`tab-${tabDef.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs transition ${tabKey === tabDef.key ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200 hover:bg-white shadow-sm"}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t(tabDef.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <TenantPicker
          value={tenantId}
          onChange={changeTenant}
          allowAll
          className="w-64"
          testId="gdv-tenant"
        />

        <div className="relative max-w-md flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t(tab.searchKey)}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            className="pl-10 bg-slate-50 border-slate-200"
            data-testid="global-search"
            maxLength={SEARCH_MAX_LENGTH}
          />
        </div>

        {tab.statuses && (
          <select
            value={statusFilter}
            onChange={(e) => changeStatus(e.target.value)}
            className={SELECT_CLASS}
            aria-label={t("gdv.allStatuses")}
            data-testid="gdv-status"
          >
            <option value="">{t("gdv.allStatuses")}</option>
            {tab.statuses.map((v) => (
              <option key={v} value={v}>{t(valueKey(tab.key, v), undefined, v)}</option>
            ))}
          </select>
        )}
      </div>

      {/* Only readable tabs are offered, so a 403 here means the grant changed after the page
          loaded: that is the shared no-access panel, never the server's English refusal text. */}
      {loadError && !loading && isForbidden(loadError) && <NoAccessPanel testId="gdv-forbidden" />}
      <LoadFailure
        message={loadError && !loading && !isForbidden(loadError) ? loadErrorMessage(loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={refresh}
        retryLabel={t("sal.retry")}
        testId="gdv-load-error"
        retryTestId="gdv-load-error-retry"
      />

      {/* While a read is in flight the failure box gives way to the spinner: a Retry that
          shows nothing happening reads as a dead button. A read that fails again brings the
          box back (`loading` ends, `loadError` is set). */}
      {loading && (loadError || !shown) && (
        <span data-testid="gdv-loading"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></span>
      )}

      {!loadError && shown && (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-6 py-3 text-xs text-slate-400 border-b border-slate-100 flex items-center gap-2">
            <span data-testid="gdv-total">{t(totalKey(lang, shown.total), { total: shown.total })}</span>
            {loading && <Loader2 className="w-3 h-3 animate-spin text-amber-600" />}
          </div>
          <GlobalDataTable
            columns={tab.columns}
            items={shown.items}
            total={shown.total}
            page={page}
            pageSize={PAGE_SIZE}
            onPage={setPage}
            sort={sort}
            onSort={changeSort}
            ctx={ctx}
            testIdPrefix="gdv"
            emptyText={t("gdv.empty")}
          />
        </div>
      )}
    </div>
  );
}
